import { useEffect, useState } from 'react'
import { Link, getRouteApi } from '@tanstack/react-router'
import { Clock, PackageCheck, Search, Star } from 'lucide-react'

import { PageIntro } from '@/components/page-intro'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardBody } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { Input, Select } from '@/components/ui/field'
import { Skeleton } from '@/components/ui/skeleton'
import { useServices } from '@/features/services/api'
import { useMarketplaceCategories } from '@/features/marketplace/api'
import { formatNumber, formatRupiah } from '@/lib/format'
import type { ServiceListing } from '@/lib/types'

const route = getRouteApi('/public/jasa')

const sortOptions = [
  { value: 'newest', label: 'Terbaru' },
  { value: 'popular', label: 'Paling banyak dipesan' },
  { value: 'rating', label: 'Rating tertinggi' },
  { value: 'price_asc', label: 'Harga termurah' },
]

export function ServicesPage() {
  const search = route.useSearch()
  const navigate = route.useNavigate()
  const [draft, setDraft] = useState(search.q ?? '')

  useEffect(() => {
    setDraft(search.q ?? '')
  }, [search.q])

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if ((search.q ?? '') === draft) return
      navigate({ search: (previous) => ({ ...previous, q: draft || undefined, page: 1 }), replace: true })
    }, 350)
    return () => window.clearTimeout(timer)
  }, [draft, navigate, search.q])

  const { data, isLoading } = useServices({
    q: search.q,
    category: search.category,
    sort: search.sort,
    page: search.page,
  })
  const { data: categories } = useMarketplaceCategories()

  const services = data?.services ?? []
  const meta = data?.meta

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-10 sm:px-6">
      <PageIntro
        accent="jasa"
        eyebrow="Jasa"
        icon={<PackageCheck className="size-3.5" />}
        title="Paket jasa"
        description="Harga, lama pengerjaan, dan jatah revisi tertulis sejak awal. Dana Anda ditahan escrow sampai hasilnya disetujui."
      />

      <div className="mt-8 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-subtle" />
          <Input
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="Cari jasa: dasbor data, desain aplikasi, penulisan…"
            aria-label="Cari jasa"
            className="pl-10"
          />
        </div>

        <Select
          value={search.sort ?? 'newest'}
          onChange={(event) => navigate({ search: (previous) => ({ ...previous, sort: event.target.value, page: 1 }) })}
          aria-label="Urutkan"
          className="sm:w-56"
        >
          {sortOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      </div>

      {categories?.length ? (
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => navigate({ search: (previous) => ({ ...previous, category: undefined, page: 1 }) })}
            className={`rounded-full border px-3.5 py-1.5 text-sm font-semibold transition ${
              !search.category ? 'border-brand bg-brand-soft text-brand-ink' : 'border-line text-muted hover:text-ink'
            }`}
          >
            Semua bidang
          </button>
          {categories.slice(0, 10).map((category) => (
            <button
              key={category.slug}
              type="button"
              onClick={() =>
                navigate({ search: (previous) => ({ ...previous, category: category.slug, page: 1 }) })
              }
              className={`rounded-full border px-3.5 py-1.5 text-sm font-semibold transition ${
                search.category === category.slug
                  ? 'border-brand bg-brand-soft text-brand-ink'
                  : 'border-line text-muted hover:text-ink'
              }`}
            >
              {category.name}
            </button>
          ))}
        </div>
      ) : null}

      <p className="mt-6 border-b border-line pb-4 text-sm text-muted" aria-live="polite">
        {isLoading ? 'Memuat jasa…' : `${formatNumber(meta?.total ?? 0)} jasa tersedia`}
      </p>

      {isLoading ? (
        <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} className="h-72 rounded-2xl" />
          ))}
        </div>
      ) : services.length === 0 ? (
        <Card className="mt-6">
          <EmptyState illustration="box"
            icon={<PackageCheck className="size-6" />}
            title="Belum ada jasa yang cocok"
            description="Coba kata kunci lain, atau posting kebutuhan Anda di papan proyek agar tenaga ahli yang menawar."
            action={
              <Link to="/proyek">
                <Button variant="outline">Buka papan proyek</Button>
              </Link>
            }
          />
        </Card>
      ) : (
        <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {services.map((service) => (
            <ServiceCard key={service.id} service={service} />
          ))}
        </div>
      )}
    </div>
  )
}

/** Kartu etalase jasa; dipakai halaman publik dan profil talenta. */
export function ServiceCard({ service }: { service: ServiceListing }) {
  const fastest = service.packages.reduce<number | null>(
    (lowest, pkg) => (lowest === null || pkg.delivery_days < lowest ? pkg.delivery_days : lowest),
    null,
  )

  return (
    <Link
      to="/jasa/$slug"
      params={{ slug: service.slug }}
      className="card group flex flex-col overflow-hidden transition duration-200 hover:-translate-y-0.5 hover:shadow-[var(--shadow-lifted)]"
    >
      <div className="aspect-[16/9] overflow-hidden bg-sunken">
        {service.thumbnail_url ? (
          <img
            src={service.thumbnail_url}
            alt=""
            loading="lazy"
            className="size-full object-cover transition duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="flex size-full items-center justify-center bg-brand-soft text-brand">
            <PackageCheck className="size-10" />
          </div>
        )}
      </div>

      <CardBody className="flex flex-1 flex-col">
        <p className="text-xs font-semibold uppercase tracking-wide text-brand">{service.category}</p>
        <h3 className="clamp-2 mt-1.5 font-bold leading-snug text-ink">{service.title}</h3>
        <p className="mt-1.5 text-sm text-muted">{service.seller.full_name}</p>

        <div className="mb-4 mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-subtle">
          {service.rating_count > 0 ? (
            <span className="inline-flex items-center gap-1 font-semibold text-ink">
              <Star className="size-3.5 fill-amber-400 text-amber-400" />
              {service.rating.toFixed(1)} ({service.rating_count})
            </span>
          ) : (
            <span>Belum ada ulasan</span>
          )}
          {fastest ? (
            <span className="inline-flex items-center gap-1">
              <Clock className="size-3.5" />
              mulai {fastest} hari
            </span>
          ) : null}
          <span>{service.order_count} pesanan</span>
        </div>

        <div className="mt-auto flex items-end justify-between gap-3 border-t border-line pt-3">
          <div>
            <p className="text-xs text-subtle">Mulai dari</p>
            <p className="text-lg font-extrabold tracking-tight text-ink">
              {formatRupiah(service.starting_price)}
            </p>
          </div>
          <Badge tone="brand">{service.packages.length} paket</Badge>
        </div>
      </CardBody>
    </Link>
  )
}
